import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Field, Input, StatTile } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductionOrderClient } from "../../api/production";
import { isoDate } from "../../api/reporting";
import { NexpimView } from "../../views";
import { ScrapTable, squareMetres } from "./ScrapTable";

const PERCENT = new Intl.NumberFormat(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * Scrap over the orders completed in a period: per virtual panel, the area the
 * engineering BOMs needed against the sheets really consumed, and per other
 * material the same in its stock unit. The last 30 days by default.
 */
export function ScrapReportPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const allowed = hasView(NexpimView.PRODUCTION_ORDERS);
  const orders = useMemo(() => new ProductionOrderClient(http), [http]);
  // Local days: toISOString is UTC, which is already tomorrow on a Montréal evening.
  const [from, setFrom] = useState(() => {
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return isoDate(start);
  });
  const [to, setTo] = useState(() => isoDate(new Date()));
  const valid = from !== "" && to !== "" && from <= to;

  const scrap = useQuery({
    queryKey: ["production", "scrap", from, to],
    queryFn: () => orders.scrapForPeriod(from, to),
    enabled: allowed && valid,
  });

  const title = t("navigation.item.production-scrap", "Scrap report");
  if (!allowed) {
    return (
      <>
        <PageHeader title={title} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  const rows = scrap.data ?? [];
  const panels = rows.filter((row) => row.kind === "PANEL");
  const sum = (pick: (row: (typeof rows)[number]) => number | null) => panels.reduce((total, row) => total + Number(pick(row) ?? 0), 0);
  const engineering = sum((row) => row.engineering);
  const consumed = sum((row) => row.consumed);
  const waste = sum((row) => row.scrap);

  return (
    <>
      <PageHeader
        title={title}
        subtitle={t("scrap.report.subtitle", "Orders completed in the period: what the engineering BOMs needed against what was consumed.")}
      />
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-col gap-4 border-b border-line p-4 lg:px-6">
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t("scrap.report.from", "From")} htmlFor="scrap-from">
              <Input id="scrap-from" type="date" value={from} max={to || undefined} onChange={(event) => setFrom(event.target.value)} />
            </Field>
            <Field
              label={t("scrap.report.to", "To")}
              htmlFor="scrap-to"
              error={valid ? undefined : t("scrap.report.invalid", "Pick a start on or before the end.")}
            >
              <Input id="scrap-to" type="date" value={to} min={from || undefined} onChange={(event) => setTo(event.target.value)} />
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <StatTile label={t("scrap.report.engineering", "Panels: engineering")} value={squareMetres(engineering)} />
            <StatTile label={t("scrap.report.consumed", "Panels: consumed")} value={squareMetres(consumed)} />
            <StatTile
              label={t("scrap.report.scrap", "Panels: scrap")}
              value={squareMetres(waste)}
              note={consumed > 0 ? t("scrap.report.scrap-share", "{{percent}} % of consumed", { percent: PERCENT.format((waste / consumed) * 100) }) : undefined}
            />
          </div>
        </div>
        <ScrapTable rows={rows} loading={scrap.isLoading && valid} showOrderCount />
      </div>
    </>
  );
}
